import {
    getQueue,
    updateQueueItem,
    dequeue,
    markQueueItemError,
    markSynced,
    resetInFlightToPending,
} from './localStore';
import { db } from './db';

const STORE_BY_ENTITY_TYPE = {
    section: 'sections',
    room: 'rooms',
    equipment: 'equipments',
};

const MAX_BACKOFF_MS = 30000;
const BASE_BACKOFF_MS = 1000;

let isDraining = false;

// Replays the sync queue strictly in insertion order (Dexie `++seq` = FIFO),
// which is what guarantees a room never replays before its parent section:
// a room can only be created in the UI after its section already exists
// locally, so the section's queue entry necessarily has a lower seq.
export async function drainQueue({ onProgress } = {}) {
    if (isDraining) return { drained: 0, remaining: 0, authRequired: false };
    isDraining = true;
    try {
        await resetInFlightToPending();
        const queue = await getQueue();
        const total = queue.filter((item) => item.status !== 'error').length;
        let processed = 0;
        let authRequired = false;
        const blockedEntityIds = new Set();

        for (const item of queue) {
            if (item.status === 'error') {
                // Permanently failed (4xx): anything depending on it must not
                // be replayed out of order.
                blockedEntityIds.add(item.entityId);
                continue;
            }

            if (item.dependsOnEntityId && blockedEntityIds.has(item.dependsOnEntityId)) {
                await markQueueItemError(item.seq, 'Dépendance non synchronisée');
                blockedEntityIds.add(item.entityId);
                continue;
            }

            if (item.attempts > 0 && item.lastAttemptAt) {
                const backoffMs = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * 2 ** item.attempts);
                if (Date.now() - item.lastAttemptAt < backoffMs) {
                    // Not ready to retry yet; ordering means nothing after this
                    // entry can safely proceed either, so stop this pass.
                    break;
                }
            }

            onProgress?.({ total, remaining: total - processed, currentEntity: item.entityId });
            await updateQueueItem(item.seq, { status: 'in_flight' });

            try {
                const response = item.kind === 'photo'
                    ? await sendPhotoItem(item)
                    : await fetch(`${process.env.REACT_APP_API_URL}${item.url}`, {
                        method: item.method,
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(item.body),
                    });

                if (response.ok) {
                    // Mark the local entity synced before dequeuing: every write this
                    // queue replays is an upsert (safe to resend), so if
                    // markEntitySynced throws, falling through to the catch below and
                    // retrying the whole item is safe — and keeps the queue entry
                    // around instead of silently losing it (dequeue-then-mark could
                    // otherwise deque a to-be-retried item whose entity never
                    // actually got marked synced).
                    await markEntitySynced(item);
                    await dequeue(item.seq);
                    processed += 1;
                } else if (response.status === 401) {
                    // Session expirée : ni erreur définitive ni tentative consommée. Le drain
                    // s'arrête (l'ordre FIFO interdit de sauter cet item) et reprend après la
                    // reconnexion (walter:authenticated, cf. useSyncOnReconnect).
                    await updateQueueItem(item.seq, { status: 'pending', lastError: 'Reconnexion nécessaire' });
                    authRequired = true;
                    break;
                } else if (response.status >= 400 && response.status < 500) {
                    await markQueueItemError(item.seq, `HTTP ${response.status}`);
                    // A failed photo upload doesn't invalidate the equipment it
                    // belongs to — nothing else in the queue depends on the
                    // photo itself, only on the equipment's own creation.
                    if (item.kind !== 'photo') blockedEntityIds.add(item.entityId);
                } else {
                    await scheduleRetry(item, `HTTP ${response.status}`);
                    break;
                }
            } catch (error) {
                await scheduleRetry(item, error.message);
                break;
            }
        }

        onProgress?.({ total, remaining: total - processed, currentEntity: null });
        return { drained: processed, remaining: total - processed, authRequired };
    } finally {
        isDraining = false;
    }
}

async function markEntitySynced(item) {
    if (item.entityType === 'usages') {
        const usages = item.body?.usages || [];
        await Promise.all(usages.map((usage) => markSynced('usages', usage._id)));
        return;
    }
    if (item.entityType === 'photo') {
        await db.equipments.update(item.entityId, { image_url: item.uploadedImageUrl });
        return;
    }
    const storeName = STORE_BY_ENTITY_TYPE[item.entityType];
    if (storeName) await markSynced(storeName, item.entityId);
}

// Two-step replay for a queued photo: upload the blob to Cloudinary, then
// attach the resulting URL to the equipment. `uploadedImageUrl` is cached on
// the queue row after step 1 so a retry that only needs step 2 never
// re-uploads the same photo (which would orphan a duplicate on Cloudinary).
async function sendPhotoItem(item) {
    let imageUrl = item.uploadedImageUrl;
    if (!imageUrl) {
        const formData = new FormData();
        formData.append('image', item.blob, item.fileName || 'equipment-photo.jpg');
        const uploadResponse = await fetch(`${process.env.REACT_APP_API_URL}/upload_image`, {
            method: 'POST',
            body: formData,
        });
        if (!uploadResponse.ok) return uploadResponse;
        const data = await uploadResponse.json();
        imageUrl = data.image_url;
        item.uploadedImageUrl = imageUrl;
        await updateQueueItem(item.seq, { uploadedImageUrl: imageUrl });
    }

    return fetch(`${process.env.REACT_APP_API_URL}/update_equipment/${item.entityId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_url: imageUrl }),
    });
}

async function scheduleRetry(item, errorMessage) {
    await updateQueueItem(item.seq, {
        status: 'pending',
        attempts: (item.attempts || 0) + 1,
        lastError: errorMessage || null,
        lastAttemptAt: Date.now(),
    });
}
